import { Configuration, TitlesApi } from '@sr-sdks/api-sdk-axios';
import {
  type QueryClient,
  queryOptions,
  skipToken,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import type { CacheRequestConfig } from 'axios-cache-interceptor';
import { configuration } from '../../configuration';
import axiosInstance, { bypassCacheConfig } from '../../utils/axiosInstance';
import { toApiError } from '../../utils/httpError';
import {
  isClientSearchTooBroad,
  shouldRetryTitlesQuery,
  tooBroadAsResult,
} from './titlesErrors';
import {
  keepPreviousForSameSearch,
  keepPreviousSearchResult,
} from './titlesPlaceholder';
import { TitlesQueryParams } from './titlesTypes';

/**
 * Default parameters for Titles queries
 */
export const TITLES_DEFAULT_PARAMS = {
  // Set default `name` to an empty string to avoid a new query being created when the search field is empty.
  name: '',
  page: 1,
  pageSize: 25,
  sortBy: 'title:asc',
} as const satisfies TitlesQueryParams;

/**
 * @internal **IMPORTANT**: Avoid exporting and prefer to use
 * a Query Options factory instead.
 */
const titlesQueryKeys = {
  all: ['titles'] as const,
  paginated: (params: TitlesQueryParams) =>
    [...titlesQueryKeys.all, 'paginated', { ...params }] as const,
  single: (titleId?: string) =>
    [...titlesQueryKeys.all, 'single', { titleId }] as const,
};

/**
 * Domain key for this module's paginated queries — derived from the query
 * key factory rather than hand-typed, so consumers that scan the cache by
 * domain (e.g. `useCachedPaginatedItem`) stay in sync with it.
 */
export const TITLES_QUERY_DOMAIN_KEY = titlesQueryKeys.all[0];

// ===== API FUNCTIONS =====

/**
 * Create a titles API instance
 */
const getTitlesApi = () => {
  return new TitlesApi(
    new Configuration(),
    configuration.TITLES_API_BASE,
    axiosInstance,
  );
};

/**
 * Drops an empty filter list so it never reaches the wire as `?x=`. The single
 * place that rule lives — callers pass their selections as-is.
 */
const toFilterArg = <T>(values?: T[]) => (values?.length ? values : undefined);

/**
 * Fetch paginated titles with optional filters
 */
export const fetchTitlesPaginated = async (
  params: TitlesQueryParams,
  options?: CacheRequestConfig,
) => {
  try {
    // Nothing else belongs here — every filter has its own SDK arg below, and
    // repeating one in `params` would put it on the wire twice.
    const requestConfig: CacheRequestConfig = {
      ...bypassCacheConfig(),
      ...options,
    };

    // Args are positional and mostly optional, so a wrong order is a silent bug
    // rather than a type error. Re-check against the generated signature after
    // any SDK regen — the order follows DTO declaration order.
    const response = await getTitlesApi().titleControllerFindAllV2(
      toFilterArg(params.claimStatuses),
      toFilterArg(params.clientIds),
      params.clientName,
      params.name,
      toFilterArg(params.ownerClientIds),
      params.seriesOnly,
      toFilterArg(params.serviceTypes),
      toFilterArg(params.titleIds),
      toFilterArg(params.titleTypes),
      // A single value, so it needs no empty-list guard: `useTitleFilters` only
      // ever holds a whole year or `undefined`.
      params.year,
      params.workspaceId,
      params.page,
      params.pageSize,
      params.sortBy,
      undefined, // xCorrelationId: the backend middleware fills the header
      requestConfig,
    );

    return response.data;
  } catch (error) {
    // Do NOT capture Sentry exceptions here; QueryCache.onError already captures.
    throw toApiError(error, {
      endpoint: 'titleControllerFindAllV2',
      params: params,
    });
  }
};

/**
 * Fetch a single title by ID
 */
export const fetchTitleById = async (id: string) => {
  try {
    const response = await getTitlesApi().titleControllerFindOne(
      id,
      bypassCacheConfig(),
    );

    return response.data;
  } catch (error) {
    // Do NOT capture Sentry exceptions here; QueryCache.onError already captures.
    throw toApiError(error, {
      endpoint: 'titleControllerFindOne',
      params: { id },
    });
  }
};

// ===== QUERY OPTIONS =====

/**
 * Returns React Query options for fetching paginated titles.
 *
 * - Merges provided params with TITLES_DEFAULT_PARAMS.
 * - If a workspaceId is present, enables the fetchTitlesPaginated query function, otherwise skips query.
 *
 * @param params - Partial or full TitlesQueryParams to control pagination, filtering, and sorting of titles
 * @param requestConfig - Per-request axios options, e.g. `suppressErrorModal`
 * @returns Query options object suitable for useQuery or similar Tanstack Query methods.
 */
export const queryOptionsGetTitlesPaginated = (
  params: TitlesQueryParams,
  requestConfig?: CacheRequestConfig,
) => {
  params = {
    ...TITLES_DEFAULT_PARAMS,
    ...params,
  };

  /*
   * requestConfig only carries per-request axios options. The only caller
   * derives it from clientName, which is already in params and so in the key.
   */
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  return queryOptions({
    // The too-broad refusal is a normal outcome the Titles page shows inline,
    // so `queryClient`'s Sentry reporting skips it.
    meta: { isExpectedError: isClientSearchTooBroad },
    placeholderData: (previousData, previousQuery) =>
      keepPreviousForSameSearch(
        params,
        previousQuery?.queryKey[2],
        previousData,
      ),
    queryFn: params.workspaceId
      ? () => fetchTitlesPaginated(params, requestConfig)
      : skipToken,
    queryKey: titlesQueryKeys.paginated(params),
    retry: shouldRetryTitlesQuery,
  });
};

/**
 * Query options for the Titles page's search. The same request as
 * {@link queryOptionsGetTitlesPaginated}, except that the too-broad refusal is
 * stored as a result ({@link TitlesTooBroad}) rather than thrown. A failed
 * query has no data and is refetched on every revisit; a stored result is
 * cached for `staleTime` like rows, so backspacing to a refused term asks
 * nothing. The union type stops any consumer reading `items` unchecked, which
 * is why the other callers keep the plain factory.
 *
 * Its key ends in `'search'` so no other consumer of the same params can be
 * handed a result without `items`.
 */
export const queryOptionsSearchTitlesPaginated = (
  params: TitlesQueryParams,
  requestConfig?: CacheRequestConfig,
) => {
  params = {
    ...TITLES_DEFAULT_PARAMS,
    ...params,
  };

  // See `queryOptionsGetTitlesPaginated` for why the key omits `requestConfig`.
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  return queryOptions({
    placeholderData: (previousData, previousQuery) =>
      keepPreviousSearchResult(
        params,
        previousQuery?.queryKey[2],
        previousData,
      ),
    queryFn: params.workspaceId
      ? () =>
          tooBroadAsResult(() => fetchTitlesPaginated(params, requestConfig))
      : skipToken,
    queryKey: [...titlesQueryKeys.paginated(params), 'search'] as const,
    retry: shouldRetryTitlesQuery,
  });
};

/**
 * Returns React Query options for fetching a single title by ID.
 *
 * - If a titleId is present, enables the fetchTitleById query function, otherwise skips query.
 *
 * @param titleId - The ID of the title to fetch
 * @returns Query options object suitable for useQuery or similar Tanstack Query methods.
 */
export const queryOptionsGetTitle = (titleId?: string) => {
  return queryOptions({
    queryFn: titleId ? () => fetchTitleById(titleId) : skipToken,
    queryKey: titlesQueryKeys.single(titleId),
  });
};

// ===== MUTATIONS =====

/**
 * Delete a title by ID
 */
export const deleteTitle = async (id: string) => {
  try {
    const response = await getTitlesApi().titleControllerRemove(id);

    return response.data;
  } catch (error) {
    throw toApiError(error, {
      endpoint: 'titleControllerRemove',
      params: { id },
    });
  }
};

/**
 * Refetches every title query (lists, searches and single titles), for a change made outside the Titles pages (e.g. an embedded workflow or a delete).
 */
export const invalidateTitleLists = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: titlesQueryKeys.all });

/**
 * Hook for deleting a title
 */
export const useDeleteTitle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTitle,
    onSuccess: (data, id) => {
      // Remove the title from cache
      queryClient.removeQueries({
        queryKey: titlesQueryKeys.single(id),
      });

      invalidateTitleLists(queryClient);
    },
  });
};
